// Spend-to-the-End solver.
//
// Given a curve, target residual, start/end window, and inflation rate, find
// the base annual spending amount A, in today's dollars, that drains liquid
// assets to the target by the end of the window without going negative along
// the way.
//
// Linear and Front-loaded curves use one Expense for the whole window. The
// Smile curve splits into three (Early/Mid/Late) so the U-shape can be
// expressed as plain Expenses (amount + annualInf) without a custom per-year
// override on the Expense model.
//
// The solver tries successive A values via binary search. Each iteration:
//   1) injects locked synthetic Expenses into `model.expenses`
//   2) projects via the existing Calculator
//   3) scores the projection (target hit? went broke? depleted early?)
// then removes them in a `finally`. The returned phase list is what the
// caller renders.

import Model from "../../model/Model"
import Person from "../../model/Person"
import Plan from "../../model/Plan"
import Calculator, {FutureCalc, IFutureYear} from "./Calculator"
import Expense, {ExpenseCategory} from "../../model/Expense"
import Schedule from "../../model/Schedule"


export enum SpendingCurve {
  Linear = "linear",
  FrontLoaded = "frontLoaded",
  Smile = "smile",
}

// A single piece of the solver-shaped spending curve. Linear / Front-loaded
// produce one phase; Smile produces three. Each phase is a plain Expense —
// amount in today's $ at currentYear, plus annualInf — so the Calculator
// processes it generically.
export interface ISpendToTheEndPhase {
  amount: number       // today's $ at currentYear; Calculator inflates forward via futureValue()
  annualInf: number    // decimal rate, e.g. 0.03 for 3% nominal per year
  start: string        // ISO date (YYYY-MM-DD)
  end: string          // ISO date
}

// Deterministic id prefix used by every synthetic Spend-to-the-End Expense; the
// breakdown chart and idempotency-filter both rely on this. Linear /
// Front-loaded produce "spend-to-the-end-1"; Smile produces "spend-to-the-end-1",
// "spend-to-the-end-2", "spend-to-the-end-3".
export const SPEND_TO_THE_END_ID_PREFIX = "spend-to-the-end"
export const spendToTheEndExpenseId = (i: number) => `${SPEND_TO_THE_END_ID_PREFIX}-${i + 1}`


const MAX_ITERATIONS = 25
const CONVERGENCE_DOLLARS = 1000
const CONVERGENCE_PCT = 0.005

// Annual real-$ decline used for the Front-Loaded curve (Bill Perkins
// "go-go years"). The Calculator's inflation math combines this with the
// household inflation rate into a single effective nominal `annualInf`.
// Exported so the view can use it as the field default / reset value.
export const FRONT_LOADED_REAL_DECLINE = 0.03

// Blanchett retirement-smile anchor averages by phase. Each phase's real-$
// spending is flat at the multiplier; we renormalize the three so the
// weighted-by-year mean across all phases = 1.0 so that the base amount A
// remains interpretable as "average annual real spend."
// Exported so the view can use them as field defaults / reset values.
export const SMILE_EARLY_MULTIPLIER = (1.15 + 1.10 + 1.00 + 0.90) / 4   // ~1.0375
export const SMILE_MID_MULTIPLIER   = (0.90 + 0.85 + 0.85 + 0.90) / 4   // 0.875
export const SMILE_LATE_MULTIPLIER  = (0.90 + 1.00 + 1.10 + 1.20) / 4   // 1.05



export interface ISpendToTheEndArgs {
  model: Model
  plan: Plan
  calculator: Calculator
  targetResidualToday: number
  curve: SpendingCurve
  startYear: number
  endYear?: number         // Defaults to person1.latestLifeExpectancyYear.
  inflationRate?: number   // Decimal override (e.g., 0.03 for 3%). Defaults to model's expense inflation rate.
  // Advisor-tunable curve variables (Advanced card). Each defaults to the
  // research constant below when omitted, so the no-override path is unchanged.
  frontLoadedRealDecline?: number  // Front-loaded: annual real-$ decline (decimal, e.g. 0.03).
  smileEarlyMultiplier?: number    // Smile: early-phase relative level (e.g. 1.0375).
  smileMidMultiplier?: number      // Smile: mid-phase relative level (e.g. 0.875).
  smileLateMultiplier?: number     // Smile: late-phase relative level (e.g. 1.05).
  signal?: AbortSignal     // If provided and aborted between iterations, the solver throws AbortError.
}

export interface ISpendToTheEndResult {
  solvedPhases: ISpendToTheEndPhase[]      // Persisted on the change + rendered in the budget.
  feasible: boolean
  warning?: string
  endLiquidToday: number
  minLiquidAcrossYears: number
  iterations: number
  startYear: number
  endYear: number
  inflationRate: number
}

export class SolverAbortError extends Error {
  constructor() {
    super("Solver aborted")
    this.name = "AbortError"
  }
}

// === Curve → base phases ===================================================

// Lays out the curve's phases for an `A = 1.0` solver iteration. The phase
// `amount` field holds the **relative weight** (multiplier) of that phase —
// the binary search later scales these by the actual A it converges on.
//
// Linear / FrontLoaded → 1 phase. Smile → 3 phases (Early/Mid/Late).
//
// The four curve-variable params are advisor overrides (Advanced card); each
// falls back to its research constant when omitted, so the no-override path is
// unchanged.
export function buildBasePhases(
  curve: SpendingCurve,
  startYear: number,
  endYear: number,
  inflationRate: number,
  curveVars: {
    frontLoadedRealDecline?: number
    smileEarlyMultiplier?: number
    smileMidMultiplier?: number
    smileLateMultiplier?: number
  } = {},
): ISpendToTheEndPhase[] {
  if (curve === SpendingCurve.Linear) {
    // Flat real $: nominal grows at the household inflation rate.
    return [{
      start: `${startYear}-01-01`,
      end: `${endYear}-12-31`,
      annualInf: inflationRate,
      amount: 1.0,
    }]
  }
  if (curve === SpendingCurve.FrontLoaded) {
    // Real $ declines ~3%/yr — combine with inflation into a single effective
    // nominal rate. effective = (1+i)(1-d) - 1.
    const realDecline = curveVars.frontLoadedRealDecline ?? FRONT_LOADED_REAL_DECLINE
    return [{
      start: `${startYear}-01-01`,
      end: `${endYear}-12-31`,
      annualInf: (1 + inflationRate) * (1 - realDecline) - 1,
      amount: 1.0,
    }]
  }

  // Smile — three phases of approximately equal length.
  let early = curveVars.smileEarlyMultiplier ?? SMILE_EARLY_MULTIPLIER
  let mid   = curveVars.smileMidMultiplier   ?? SMILE_MID_MULTIPLIER
  let late  = curveVars.smileLateMultiplier  ?? SMILE_LATE_MULTIPLIER

  const yearCount = Math.max(1, endYear - startYear + 1)
  const p1Len = Math.max(1, Math.ceil(yearCount / 3))
  const p2Len = Math.max(1, Math.ceil((yearCount - p1Len) / 2))
  const p3Len = Math.max(1, yearCount - p1Len - p2Len)

  // Renormalize anchor averages so weighted (by year count) mean = 1.0,
  // keeping A interpretable as the mean annual real spend.
  let weightedSum = p1Len * early + p2Len * mid + p3Len * late
  // Defensive: a degenerate override set (all zero / non-finite, e.g. from a
  // hand-edited settings bag) would make norm Infinity → NaN amounts. Fall back
  // to the default multipliers so the projection stays valid.
  if (!(weightedSum > 0) || !isFinite(weightedSum)) {
    early = SMILE_EARLY_MULTIPLIER
    mid = SMILE_MID_MULTIPLIER
    late = SMILE_LATE_MULTIPLIER
    weightedSum = p1Len * early + p2Len * mid + p3Len * late
  }
  const norm = yearCount / weightedSum

  const p1End = startYear + p1Len - 1
  const p2End = p1End + p2Len
  return [
    { start: `${startYear}-01-01`, end: `${p1End}-12-31`,  annualInf: inflationRate, amount: early * norm },
    { start: `${p1End + 1}-01-01`, end: `${p2End}-12-31`,  annualInf: inflationRate, amount: mid   * norm },
    { start: `${p2End + 1}-01-01`, end: `${endYear}-12-31`, annualInf: inflationRate, amount: late  * norm },
  ]
}

// === Synthetic Expense attach/detach =======================================

// Push transient, locked Spend-to-the-End Expenses into the model so projections
// (and the chart) treat the strategy spend as real, categorized line items.
// Returns a cleanup that removes them again. The solver calls this once per
// binary-search iteration; the view calls it around its chart-render too.
// All injection is transient — there is no persistent application path.
export function attachSpendToTheEndPhases(model: Model, phases: ISpendToTheEndPhase[]): () => void {
  // Drop any prior copies so we never have duplicates of the synthetic rows.
  model.expenses = model.expenses.filter((e) => !e.id.startsWith(SPEND_TO_THE_END_ID_PREFIX))
  if (phases.length === 0) return () => {}

  const owner = model.persons?.[0]
  const showSuffix = phases.length > 1
  const suffixes = ["Early", "Mid", "Late"]
  const ids: string[] = []

  phases.forEach((phase, i) => {
    const id = spendToTheEndExpenseId(i)
    const description = showSuffix
      ? `Spend to the End — ${suffixes[i] ?? `Phase ${i + 1}`}`
      : "Spend to the End"
    model.expenses.push(makeSpendToTheEndExpense(model, owner, id, description, phase))
    model.addLock(id, "*", "Locked by Spend to the End")
    ids.push(id)
  })

  return () => {
    model.expenses = model.expenses.filter((e) => !ids.includes(e.id))
    ids.forEach((id) => model.deleteLock(id, "*"))
  }
}

function makeSpendToTheEndExpense(model: Model, owner: Person | undefined, id: string, description: string, phase: ISpendToTheEndPhase): Expense {
  const nowIso = new Date().toISOString()
  return new Expense({
    id,
    createdAt: nowIso,
    updatedAt: nowIso,
    accountId: model.accountId,
    userId: model.userId,
    modelId: model.id,
    model: model,                                          // needed for endDate getter
    expenseCategory: ExpenseCategory.GivingAndMisc,
    description,
    amount: phase.amount,
    annualInf: phase.annualInf,
    discretionary: true,
    ownerId: owner?.id,
    owner: owner,                                          // life-expectancy fallback
    // Use "Yearly" (which exists in defaultSchedules) — ScheduleNames.Annual is
    // the user-facing label but has no entry, so getDefaultSchedule would return
    // undefined and produce an Expense with zero occurrences per year.
    schedule: Schedule.getDefaultSchedule("Yearly"),
    start: phase.start,
    end: phase.end,
  })
}

// === Solver ================================================================

// Yields to the event loop so the UI can paint between solver iterations.
// Each `calculator.calculateFuture` call is the expensive part (~50-150ms);
// yielding before it lets the spinner animate and keeps the modal interactive.
const yieldToUI = () => new Promise<void>((resolve) => setTimeout(resolve, 0))

export async function solveSpendToTheEnd(args: ISpendToTheEndArgs): Promise<ISpendToTheEndResult> {
  const { model, calculator, targetResidualToday, curve } = args
  const currentYear = new Date().getFullYear()
  const inflationRate = args.inflationRate ?? model.inflationStrategy?.expense?.rate ?? Model.defaultInflationRate

  // === 1. Validate inputs ===
  const person1 = model.persons?.[0]
  if (!person1 || !person1.birthDate) {
    return infeasible("No primary person on model.", currentYear, currentYear, inflationRate)
  }
  if (person1.lifeExpectancy <= 0) {
    return infeasible("Set a life expectancy before using Spend to the End.", currentYear, currentYear, inflationRate)
  }
  const startYear = Math.max(currentYear, args.startYear)
  const endYear = args.endYear ?? person1.latestLifeExpectancyYear
  if (startYear >= endYear) {
    return infeasible("Start year must be before end year.", startYear, endYear, inflationRate)
  }

  // === 2. Lay out the curve as base phases (multipliers as amounts; A=1.0) ===
  const basePhases = buildBasePhases(curve, startYear, endYear, inflationRate, {
    frontLoadedRealDecline: args.frontLoadedRealDecline,
    smileEarlyMultiplier: args.smileEarlyMultiplier,
    smileMidMultiplier: args.smileMidMultiplier,
    smileLateMultiplier: args.smileLateMultiplier,
  })

  // === 3. Helpers: phasesForA scales the base phases by A; evaluate runs one
  //        projection and scores it. detachExtras is rebound by each evaluate
  //        and finally-detached at the end so the model is left clean. ===
  const inflationFactor = (y: number) => Math.pow(1 + inflationRate, y - currentYear)
  const tolerance = Math.max(CONVERGENCE_DOLLARS, CONVERGENCE_PCT * Math.max(1, Math.abs(targetResidualToday)))
  let detachExtras: (() => void) | undefined

  const phasesForA = (A: number): ISpendToTheEndPhase[] =>
    A <= 0 ? [] : basePhases.map((p) => ({ ...p, amount: p.amount * A }))

  const evaluate = async (A: number) => {
    const phases = phasesForA(A)
    detachExtras = attachSpendToTheEndPhases(model, phases)
    calculator.lastFutureCalc = undefined  // synthetic expenses changed; bust cache
    await yieldToUI()
    if (args.signal?.aborted) throw new SolverAbortError()
    const fc: FutureCalc = calculator.calculateFuture(model, "SpendToTheEnd", endYear)
    return scoreProjection(fc, startYear, endYear, targetResidualToday, tolerance, inflationFactor, phases)
  }

  try {
    // === 4. Baseline: A = 0. Bail if the plan can't even sustain essentials. ===
    const base = await evaluate(0)
    if (base.minLiquid < 0) {
      return infeasible(
        "Your plan can't sustain extra spending — essential expenses already exhaust liquid assets before life expectancy.",
        startYear, endYear, inflationRate,
      )
    }
    if (base.endLiquidToday < targetResidualToday) {
      return {
        solvedPhases: [],
        feasible: false,
        warning: `Your plan ends with $${Math.round(base.endLiquidToday).toLocaleString()} in today's dollars, which is below your target of $${Math.round(targetResidualToday).toLocaleString()}. No extra spending is available.`,
        endLiquidToday: base.endLiquidToday,
        minLiquidAcrossYears: base.minLiquid,
        iterations: 1,
        startYear,
        endYear,
        inflationRate,
      }
    }

    // === 5. Binary search for the largest feasible A ===
    const yearCount = Math.max(1, endYear - startYear + 1)
    let lower = 0
    let upper = Math.max(1000, ((base.endLiquidToday - targetResidualToday) / yearCount) * 4)
    let best = base
    let bestA = 0
    let iterations = 1

    for (let i = 0; i < MAX_ITERATIONS; i++) {
      const mid = (lower + upper) / 2
      const result = await evaluate(mid)
      iterations++

      // Too aggressive: either depletes below zero, or reaches the target
      // floor before endYear (which would otherwise trivially "converge" the
      // moment liquid assets first touch the floor).
      if (result.minLiquid < 0 || result.prematureDepletion) {
        upper = mid
        continue
      }

      const diff = result.endLiquidToday - targetResidualToday
      if (Math.abs(diff) < Math.abs(best.endLiquidToday - targetResidualToday)) {
        best = result
        bestA = mid
      }
      if (Math.abs(diff) <= tolerance) {
        best = result
        bestA = mid
        break
      }
      if (diff > 0) lower = mid     // ended above target → spend more
      else          upper = mid     // ended below target → spend less
    }

    // === 6. ±5% monotonicity sanity check (tax cliffs can break it) ===
    let warning: string | undefined
    if (bestA > 0) {
      const high = await evaluate(bestA * 1.05)
      iterations++
      if (high.minLiquid >= 0 && !high.prematureDepletion && high.endLiquidToday > best.endLiquidToday + tolerance) {
        warning = "Tax thresholds may cause non-smooth solver behavior near this spending level — result is the best estimate found."
      }
    }
    if (iterations >= MAX_ITERATIONS + 2 && Math.abs(best.endLiquidToday - targetResidualToday) > tolerance) {
      warning = warning ?? "Solver did not fully converge — result is the best feasible estimate."
    }

    // === 7. Return ===
    return {
      solvedPhases: best.phases,
      feasible: true,
      warning,
      endLiquidToday: best.endLiquidToday,
      minLiquidAcrossYears: best.minLiquid,
      iterations,
      startYear,
      endYear,
      inflationRate,
    }
  } finally {
    // === Detach the solver's synthetic Expenses so the model is left clean. ===
    detachExtras?.()
    calculator.lastFutureCalc = undefined
  }
}

// === Internals =============================================================

// Scores a single projection. Returns:
//   phases               the phase list used to produce this projection
//   endLiquidToday       liquid assets at endYear in today's $
//   minLiquid            nominal floor across the spending window
//   prematureDepletion   true if any year BEFORE endYear already reached the
//                        target floor — without this check, target=$0 trivially
//                        "converges" the moment assets first hit zero, even
//                        decades early.
function scoreProjection(
  fc: FutureCalc,
  startYear: number,
  endYear: number,
  targetResidualToday: number,
  tolerance: number,
  inflationFactor: (y: number) => number,
  phases: ISpendToTheEndPhase[],
) {
  const last = fc.futureYears[fc.futureYears.length - 1]
  const endLiquidToday = last ? last.netLiquidAssets / inflationFactor(last.year) : 0
  const prematureFloor = targetResidualToday + tolerance
  let minLiquid = Infinity
  let prematureDepletion = false
  fc.futureYears.forEach((fy: IFutureYear) => {
    if (fy.year < startYear) return
    if (fy.netLiquidAssets < minLiquid) minLiquid = fy.netLiquidAssets
    if (fy.year < endYear) {
      const liquidToday = fy.netLiquidAssets / inflationFactor(fy.year)
      if (liquidToday <= prematureFloor) prematureDepletion = true
    }
  })
  return {
    phases,
    endLiquidToday,
    minLiquid: minLiquid === Infinity ? 0 : minLiquid,
    prematureDepletion,
  }
}

function infeasible(warning: string, startYear: number, endYear: number, inflationRate: number): ISpendToTheEndResult {
  return {
    solvedPhases: [],
    feasible: false,
    warning,
    endLiquidToday: 0,
    minLiquidAcrossYears: 0,
    iterations: 0,
    startYear,
    endYear,
    inflationRate,
  }
}
